import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { NgFor } from '@angular/common';
import { IconComponent } from './icon.component';

/**
 * The announcement bar: every message in one row that scrolls in an endless
 * loop. The row is rendered twice side by side and slides by exactly one copy,
 * so the loop has no seam; each copy repeats the messages until it is wider
 * than the screen. Scrolls the other way in RTL, pauses on hover, and stands
 * still (wrapped) for visitors who prefer reduced motion.
 */
@Component({
  selector: 'app-announcement-marquee',
  standalone: true,
  imports: [NgFor, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    :host { display: flex; align-items: center; gap: 10px; overflow: hidden; }
    .viewport { flex: 1; min-width: 0; overflow: hidden;
      mask-image: linear-gradient(90deg, transparent, #000 24px, #000 calc(100% - 24px), transparent);
      -webkit-mask-image: linear-gradient(90deg, transparent, #000 24px, #000 calc(100% - 24px), transparent); }
    .track { display: flex; width: max-content; animation: marquee var(--dur) linear infinite; }
    :host(:hover) .track { animation-play-state: paused; }
    :host-context([dir=rtl]) .track { animation-name: marquee-rtl; }
    .copy { display: flex; flex-shrink: 0; }
    .msg { white-space: nowrap; padding-inline-end: 22px; }
    .msg::after { content: '•'; padding-inline-start: 22px; opacity: .55; }
    @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
    @keyframes marquee-rtl { from { transform: translateX(0); } to { transform: translateX(50%); } }
    @media (prefers-reduced-motion: reduce) {
      .track { animation: none; width: auto; flex-wrap: wrap; }
      .copy + .copy { display: none; }
      .copy { flex-wrap: wrap; }
    }
  `],
  template: `
    <app-icon name="solar:volume-loud-bold" [size]="iconSize" [color]="iconColor"></app-icon>
    <div class="viewport">
      <div class="track" [style.--dur]="duration + 's'">
        <div class="copy" *ngFor="let c of [0, 1]" [attr.aria-hidden]="c === 1 ? 'true' : null">
          <span class="msg" *ngFor="let m of loop">{{ m }}</span>
        </div>
      </div>
    </div>`,
})
export class AnnouncementMarqueeComponent {
  @Input() set messages(value: string[]) {
    this.list = (value || []).filter((m) => !!m && !!m.trim());
    const chars = this.list.join('').length || 1;
    // Repeat until one copy is comfortably wider than a wide screen (~220 chars).
    const reps = Math.max(1, Math.ceil(220 / chars));
    this.loop = Array.from({ length: reps }, () => this.list).flat();
    // ~5.5 chars per second of reading speed, never faster than 14s per loop.
    this.duration = Math.max(14, Math.round(this.loop.join('').length / 5.5));
  }
  @Input() iconSize = 16;
  @Input() iconColor = '#fff';

  list: string[] = [];
  loop: string[] = [];
  duration = 20;
}
